<template>
  <div>
    <div class="tw-flex tw-justify-between tw-items-center">
      <div class="tw-text-xl tw-font-light tw-my-auto">Data Integrity</div>
      <div class="tw-flex tw-items-center">
        <base-button type="text" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters
        </base-button>
        <export-button
          class="tw-ml-5"
          size="sm"
          type="primary"
          label="Export"
          :filename="`ZeroesIntegrityReport.csv`"
          :requestPayload="exportRequestPayload"
          :url="`/businesses/${businessId}/reports/zeroes/export`"
          @onError="didErrorExport"
        />
      </div>
    </div>

    <filter-display class="tw-mt-3" :filters="filters" />

    <!-- Aggregate Summary Cards -->
    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-4 tw-mt-4">
      <aggregate-list
        v-for="agg in aggregates"
        :key="agg.status"
        class="summarylist"
        :aggregates="[agg]"
        :isLoading="isLoading"
        labelKey="status"
        idKey="status"
        valueKey="count"
        :title="agg.status"
      />
    </div>

    <base-table :loading="isLoading" :fields="fields" :items="patients" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" :loadingRows="5" class="tw-mt-2">
      <template #patient="{ item }">
        <div class="tw-flex">
          <base-button type="text" size="md" @click="viewPatient(item.userId)">
            <span class="tw-whitespace-nowrap tw-text-left">{{ item.patientName }}</span>
          </base-button>
        </div>
      </template>

      <template #plan="{ item }">
        <div class="tw-whitespace-nowrap">{{ item.planName }}</div>
        <base-badge class="tw-ml-2 tw-my-auto" :color="statusColor(item.planStatus)" size="sm">{{ capitalize(item.planStatus).replace(/_/g, " ") }}</base-badge>
      </template>

      <template #prescription="{ item }">
        <div class="tw-whitespace-nowrap">{{ item.prescriptionName }}</div>
        <base-badge class="tw-ml-2 tw-my-auto" :color="prescriptionStatusColor(item.prescriptionStatus)" size="sm">{{
          capitalize(item.prescriptionStatus).replace(/_/g, " ")
        }}</base-badge>
      </template>

      <template #activity="{ item }">
        <div class="tw-flex tw-items-center">
          <span v-if="item.hasActivity" class="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-800">
            True
          </span>
          <span v-else class="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-red-100 tw-text-red-800"> False </span>
        </div>
      </template>

      <template #status="{ item }">
        <base-badge size="sm" :color="integrityStatusColor(item.integrityStatus)" class="tw-whitespace-nowrap">
          {{ capitalize(item.integrityStatus).replace(/_/g, " ") }}
        </base-badge>
      </template>
      <template #formula="{ item }">
        <span class="tw-whitespace-nowrap tw-text-sm">{{ item.formula }}</span>
      </template>
      <template #note="{ item }">
        <span v-if="item.integrityNote" class="tw-text-xs tw-text-gray-500 tw-italic">
          {{ item.integrityNote }}
        </span>
        <span v-else class="tw-text-gray-300">—</span>
      </template>

      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">No records matching the current integrity criteria.</div>
      </template>
    </base-table>

    <base-pagination v-model="page" class="tw-mt-2" />

    <report-filter-slideover
      ref="filterSlideOver"
      :business-id="businessId"
      :show-date-filter="false"
      :show-locations-filter="false"
      :show-programs-filter="false"
      :show-program-groups-filter="false"
      :show-prescription-status-filter="false"
      :show-plans-filter="false"
      :show-plan-groups-filter="false"
      :show-plan-status-filter="false"
      :showIntegrityStatusFilter="true"
      :showFormulaFilter="true"
      @filter-updated="applyFilter"
    />
  </div>
</template>

<script>
import AggregateList from "@/views/business/reports/components/AggregateList";
import ExportButton from "@/components/ExportButton.vue";
import ReportFilterSlideover from "@/views/business/reports/components/ReportFilterSlideover";
import FilterDisplay from "@/views/business/reports/components/FilterDisplay";
import { FilterIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import { capitalize } from "lodash";

export default {
  name: "BusinessZeroesReportView",
  components: {
    AggregateList,
    ExportButton,
    ReportFilterSlideover,
    FilterDisplay,
    FilterIcon,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      patients: [],
      aggregates: [],
      page: {
        take: 50,
        skip: 0,
        total: 0,
      },
      isLoading: false,
      filters: {
        integrityStatusFilter: [],
        formulasFilter: [],
      },
      sortKey: "patientName",
      sortDesc: false,
      capitalize,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    fields() {
      return [
        { title: "Patient", key: "patient", isSortable: false },
        { title: "Plan", key: "plan", isSortable: false },
        { title: "Prescription", key: "prescription", isSortable: false },
        { title: "Has Activity", key: "activity", isSortable: false },
        { title: "Formula", key: "formula", isSortable: false },
        { title: "Status", key: "status", isSortable: false },
        { title: "Note", key: "note", isSortable: false },
      ];
    },
    searchRequestPayload() {
      let statusesFilterValues = this.filters.integrityStatusFilter
        ? this.filters.integrityStatusFilter.map(function(item) {
            // Check if it's an object or just the string value
            return item["value"] !== undefined ? item["value"] : item;
          })
        : [];

      let formulasFilterValues = this.filters.formulasFilter
        ? this.filters.formulasFilter.map(function(item) {
            return item["value"] !== undefined ? item["value"] : item;
          })
        : [];

      return {
        sortColumn: this.sortKey,
        sortOrder: this.sortDesc ? "desc" : "asc",
        statuses: statusesFilterValues,
        formulas: formulasFilterValues,
      };
    },
    searchRequestParams() {
      return {
        skip: this.page.skip,
        take: this.page.take,
      };
    },
    exportRequestPayload() {
      let statusesFilterValues = this.filters.integrityStatusFilter
        ? this.filters.integrityStatusFilter.map(function(item) {
            // Check if it's an object or just the string value
            return item["value"] !== undefined ? item["value"] : item;
          })
        : [];

      let formulasFilterValues = this.filters.formulasFilter
        ? this.filters.formulasFilter.map(function(item) {
            return item["value"] !== undefined ? item["value"] : item;
          })
        : [];

      return {
        statuses: statusesFilterValues,
        formulas: formulasFilterValues,
      };
    },
  },
  watch: {
    page() {
      this.loadPage();
    },
    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
  },
  created() {
    this.loadPage();
  },
  methods: {
    openFilters() {
      this.$refs.filterSlideOver.show();
    },
    applyFilter(filters) {
      this.filters = filters;
      this.loadPage();
    },
    loadPage() {
      this.isLoading = true;
      const url = `/businesses/${this.businessId}/reports/zeroes/search`;
      this.$axios
        .post(url, this.searchRequestPayload, { params: this.searchRequestParams })
        .then((res) => {
          this.patients = res.data.page.items;
          this.page.total = res.data.page.total;
          this.aggregates = res.data.aggregates;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load Zeroes report",
            message: "There was a problem retrieving data integrity information.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    integrityStatusColor(status) {
      switch (status) {
        case "Good":
          return "green";
        case "Bad":
          return "red";
        case "Investigate":
          return "yellow";
        case "Neutral":
          return "gray";
        default:
          return "gray";
      }
    },
    statusColor(status) {
      switch ((status || "").toLowerCase()) {
        case "active":
        case "activated - pending start":
        case "active - payments complete":
          return "green";
        case "failed payment":
        case "fp":
          return "red";
        case "paused":
        case "cancelled":
        case "awaiting payment":
        case "n/a":
          return "gray";
        default:
          return "gray";
      }
    },
    prescriptionStatusColor(status) {
      switch ((status || "").toLowerCase()) {
        case "deleted":
        case "expired":
        case "n/a":
          return "gray";
        default:
          return "green";
      }
    },
    viewPatient(userId) {
      window.open(`/businesses/${this.businessId}/members/${userId}/plans`, "_blank");
    },
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Export Failed",
        message: "Failed to download the Zeroes Integrity CSV.",
        error: err,
      });
    },
  },
};
</script>

<style scoped>
.summarylist {
  min-height: 100px;
}
</style>
